import clsx from 'clsx';
import { motion } from 'framer-motion';
import { ElementsButton } from '../Button';
import { Tooltip, TooltipContainer } from '../Tooltip';
import { ShortcutLegendIcon } from '../ShortcutLegendIcon';
import { floatingTagListVariants } from '../MessageExpandableTagList/animation/variants';
import { useMessageExpandableTagList, UseMessageExpandableTagListArgs } from './hooks/useMessagingExpandableTagList';
import { File as GQLFile } from '../../../generated/graphql';

export interface MessageExpandableTagListProps extends UseMessageExpandableTagListArgs {
  layoutIDPrefix: string;
  disableAnimation?: boolean;
  className?: string | ((expanded: boolean) => string);
  tagItemRenderer: (a: { file: GQLFile | undefined; onboardingReminder: boolean | undefined }) => React.ReactNode;
}

/**
 * @param layoutIDPrefix - Motion layout ID prefix to differentiate between multiple instances of the component
 */
export function MessageExpandableTagList({
  tagItems,
  className,
  tagItemRenderer,
  layoutIDPrefix,
  disableAnimation,
  floatingOverrides,
  verticalPlacement,
  expandedTagListID,
  setExpandedTagListID,
}: MessageExpandableTagListProps) {
  const {
    refs,
    floatingX,
    floatingY,
    tMessaging,
    reversedTagItems,
    thumbnailTagItems,
    floatingHeight,
    expanded,
    tagListID,
  } = useMessageExpandableTagList({
    tagItems,
    floatingOverrides,
    verticalPlacement,
    expandedTagListID,
    setExpandedTagListID,
  });

  return (
    <>
      <motion.div
        initial="closed"
        ref={refs.setFloating}
        custom={{ x: floatingX, y: floatingY }}
        variants={floatingTagListVariants}
        layout={disableAnimation ? undefined : 'position'}
        layoutId={`${layoutIDPrefix}-file-attachment-list`}
        animate={expanded ? 'open' : 'closed'}
        className={clsx(
          typeof className === 'string' ? className : className?.(expanded),
          'flex flex-wrap gap-2 empty:hidden',
          !expanded
            ? 'mt-2 md:w-full max-h-[136px] max-w-[calc(100vw_-_129px)]'
            : 'max-h-[calc(100dvh_-_82px)] overflow-x-hidden h-auto overflow-y-auto p-4 !z-[21] absolute'
        )}
      >
        {(expanded ? reversedTagItems : thumbnailTagItems).map((tagItem) =>
          tagItemRenderer({
            file: tagItem.type === 'file' ? tagItem.file : undefined,
            onboardingReminder: tagItem.type === 'onboardingReminder',
          })
        )}
        {tagItems?.length > 2 && (
          <>
            {expanded ? (
              <Tooltip
                placement="top"
                offset={[0, 8]}
                floatingElement={
                  <TooltipContainer className="!bg-darkMode-gray-dropdown">
                    <ShortcutLegendIcon className="!ml-0" shortcutCharacter="ESC" />
                  </TooltipContainer>
                }
              >
                <ElementsButton
                  layout
                  type="button"
                  size="small"
                  theme="text"
                  iconPosition="left"
                  icon="arrowDownward"
                  onClick={() => setExpandedTagListID?.(undefined)}
                  label={tMessaging('client-messaging-general-hide-attachments')}
                  layoutId={`${layoutIDPrefix}-file-attachment-visibility-toggle`}
                />
              </Tooltip>
            ) : (
              <ElementsButton
                layout
                type="button"
                size="small"
                theme="text"
                icon="arrowUpward"
                iconPosition="left"
                onClick={() => setExpandedTagListID?.(tagListID)}
                layoutId={`${layoutIDPrefix}-file-attachment-visibility-toggle`}
                label={tMessaging('client-messaging-general-show-more-attachments', { count: tagItems.length - 2 })}
              />
            )}
          </>
        )}
      </motion.div>
      {verticalPlacement === 'top' && (
        <>
          <div ref={refs.setPositionReference} />
          <div style={{ height: expanded ? floatingHeight : 0 }} />
        </>
      )}
      {verticalPlacement === 'bottom' && (
        <>
          <div style={{ height: expanded ? floatingHeight : 0 }} />
          <div ref={refs.setPositionReference} />
        </>
      )}
    </>
  );
}
